import React, { useState, useEffect, useMemo } from "react";
import { FaTimes, FaPlus, FaMinus, FaCube, FaHashtag, FaWifi, FaBan } from "react-icons/fa";
import { CustomDropdown, NumberInput, Textarea, FirmDropdown } from "../../atoms";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

const QuotaOperationDrawer = ({
  open,
  onClose,
  onSubmit,
  type: typeProp,
  products,
  productInfoes = [],
  quotaArray = [],
  devices = [],
  isLoading,
  // İŞLEM YAPILACAK FİRMA: Faturalar sekmesinde birden fazla firma seçiliyse
  // kota işlemi hangi firmaya gideceği burada seçilir. Liste YALNIZCA üstte
  // seçili firmalardan oluşur; tek firma seçiliyse bu blok hiç basılmaz ve
  // davranış eskisi gibi kalır.
  firms = [],
  selectedFirmId = null,
  onFirmChange,
}) => {
  // Giriş/çıkış kayması + kapanışta işlem tipinin (yükleme/iade) dondurulması;
  // tip değişince başlık ve renkler değiştiği için kapanırken göze çarpıyordu.
  const { mounted, entered, data: type } = useDrawerTransition(open, typeProp);

  const [productName, setProductName] = useState("");
  const [quota, setQuota] = useState(0);
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});

  const isUpload = type === "upload";
  const productOptions = (products || []).map((p) => ({
    id: p.productName,
    label: p.productName,
    value: p.productName,
  }));

  const selectedProductInfo = useMemo(() => {
    if (!productName) return null;
    return Array.isArray(productInfoes) ? productInfoes.find((p) => p.productName === productName) : null;
  }, [productName, productInfoes]);

  const distributedQuota = useMemo(() => {
    if (!productName || !Array.isArray(quotaArray)) return 0;
    const item = quotaArray.find((q) => q.name === productName);
    return item?.quota ?? 0;
  }, [productName, quotaArray]);

  const firmQuota = selectedProductInfo?.quota ?? 0;
  const undistributedQuota = Math.max(0, firmQuota - distributedQuota);

  const { connectedQuota, disconnectedQuota } = useMemo(() => {
    if (!productName || !Array.isArray(devices)) return { connectedQuota: 0, disconnectedQuota: 0 };
    let connected = 0;
    let disconnected = 0;
    devices.forEach((device) => {
      const item = device.productInfo?.productInfo?.find((p) => p.productName === productName);
      const q = item?.quota ?? 0;
      const isConnected = device.deviceInfo?.connection?.status;
      if (isConnected) connected += q;
      else disconnected += q;
    });
    return { connectedQuota: connected, disconnectedQuota: disconnected };
  }, [productName, devices]);

  const totalQuota = undistributedQuota + connectedQuota + disconnectedQuota;

  useEffect(() => {
    if (!open) {
      setProductName("");
      setQuota(0);
      setNote("");
      setErrors({});
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const validate = () => {
    const tempErrors = {};
    if (!productName) tempErrors.productName = "Lütfen bir ürün seçin.";
    if (quota <= 0) tempErrors.quota = "Kota miktarı 0'dan büyük olmalıdır.";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ productName, quota, note });
    }
  };

  const handleProductChange = (option) => {
    setProductName(option?.value || "");
    if (errors.productName) setErrors((p) => ({ ...p, productName: undefined }));
  };

  const IconComponent = isUpload ? FaPlus : FaMinus;
  // Faturalar (ModernBillForm) ile aynı header/footer renkleri
  const headerBg = isUpload ? "bg-emerald-50 border-b border-emerald-100" : "bg-amber-50 border-b border-amber-100";
  const iconCircle = isUpload ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600";
  const submitButtonClass = isUpload
    ? "bg-green-500 hover:bg-green-600 shadow-md"
    : "bg-red-500 hover:bg-red-600 shadow-md";

  return (
    <>
      {/* Panel — Faturalar (ModernBillForm) stiliyle uyumlu drawer */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[460px] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col bg-white`}
      >
        {/* Cevap gelene kadar tam ekran loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-emerald-500 rounded-full animate-spin" />
            <p className="mt-4 text-sm font-medium text-gray-700">İşleniyor...</p>
            <p className="mt-1 text-xs text-gray-500">Kota bağlı cihazlar arasında senkronize ediliyor</p>
          </div>
        )}
        <div className={`${headerBg} px-5 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 text-lg rounded-full ${iconCircle}`}>
                <IconComponent className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                {isUpload ? "Bedelsiz Kota Yükle" : "Kota İade Al"}
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="p-2 rounded-full hover:bg-gray-200 text-gray-500 disabled:opacity-50 transition-colors"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {firms.length > 1 && (
              <div>
                <label className="block text-sm font-SemiBold text-gray-700 mb-2">
                  İşlem Yapılacak Firma
                </label>
                <FirmDropdown
                  compact
                  firms={firms}
                  showAllOption={false}
                  value={firms.find((f) => f._id === selectedFirmId) || null}
                  onChange={(f) => onFirmChange?.(f?._id)}
                  placeholder="Firma seçin"
                  disabled={isLoading}
                />
                <p className="mt-1.5 text-[11px] text-gray-400">
                  Kota bu firmanın havuzuna işlenir; aşağıdaki bakiyeler de o
                  firmaya aittir.
                </p>
              </div>
            )}
            <CustomDropdown
              label={
                <span className="text-base font-medium text-gray-600 flex items-center gap-2">
                  <FaCube /> Ürün
                </span>
              }
              options={productOptions}
              value={productName ? { id: productName, label: productName, value: productName } : null}
              onChange={handleProductChange}
              placeholder="Ürün Seçiniz"
              searchPlaceholder="Ürün ara..."
              error={!!errors.productName}
              errorMessage={errors.productName}
              required
            />

            {productName && (
              <div className={`rounded-xl border-2 p-4 ${isUpload ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                  Kota durumu — {productName}
                </p>
                <p className="text-2xl sm:text-3xl font-bold tabular-nums text-gray-800">
                  {totalQuota.toLocaleString("tr-TR")}
                  <span className="text-base font-medium text-gray-500 ml-1.5">adet</span>
                </p>
                <div className="mt-3 pt-3 border-t border-gray-200/80 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Dağıtılmamış</span>
                    <span className="font-semibold tabular-nums text-amber-600">{undistributedQuota.toLocaleString("tr-TR")}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <FaWifi className="w-3 h-3 text-emerald-500" /> Bağlı cihazlar
                    </span>
                    <span className="font-semibold tabular-nums text-emerald-600">{connectedQuota.toLocaleString("tr-TR")}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <FaBan className="w-3 h-3 text-gray-400" /> Bağlı değil
                    </span>
                    <span className="font-semibold tabular-nums text-gray-600">{disconnectedQuota.toLocaleString("tr-TR")}</span>
                  </div>
                </div>
              </div>
            )}

            <NumberInput
              label={
                <span className="text-base font-medium text-gray-600 flex items-center gap-2">
                  <FaHashtag /> Miktar
                </span>
              }
              value={quota}
              onChange={(value) => {
                setQuota(value);
                if (errors.quota) setErrors((p) => ({ ...p, quota: undefined }));
              }}
              placeholder="Miktar girin"
              min={0}
              error={!!errors.quota}
              errorMessage={errors.quota}
              required
            />

            <Textarea
              label={
                <span className="text-base font-medium text-gray-600 flex items-center gap-2">
                  Açıklama (opsiyonel)
                </span>
              }
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isUpload ? "Bedelsiz yükleme nedeni / not..." : "İade nedeni / not..."}
              rows={3}
            />
          </div>

          <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50"
            >
              <FaTimes className="w-3.5 h-3.5" />
              İptal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-base font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed ${submitButtonClass}`}
            >
              {isLoading ? (
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <IconComponent className="w-4 h-4" />
              )}
              {isLoading ? "İşleniyor..." : isUpload ? "Yükle" : "İade Al"}
            </button>
          </div>
        </form>
      </div>

      {/* Overlay — OperationDrawer ile aynı sıra ve stil */}
      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300 ease-in-out ${entered ? "opacity-100" : "opacity-0"}`}
          onClick={isLoading ? undefined : onClose}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default QuotaOperationDrawer;
